import { useState } from 'react';
import { dismissMaintenanceItem, getSavedGuide } from '../../utils/storage';

const SERVICE_TYPES = ['DIY', 'Independent', 'Dealer'];

const DISMISS_REASONS = [
  'Already completed',
  'Not applicable to my vehicle',
  'Will do later',
  'Disagree with recommendation',
];

export default function MaintenanceCard({ item, vehicleId, skillLevel, onMarkComplete, onToast, onShowDIYGuide, canLog }) {
  const [expanded, setExpanded] = useState(false);
  const [showComplete, setShowComplete] = useState(false);
  const [showIgnore, setShowIgnore] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [completeData, setCompleteData] = useState({
    date: new Date().toISOString().split('T')[0],
    mileage: '',
    cost: '',
    type: 'DIY',
    notes: '',
  });

  function handleSubmitComplete(e) {
    e.preventDefault();
    onMarkComplete({
      service: item.service,
      date: completeData.date,
      mileage: completeData.mileage ? parseInt(completeData.mileage, 10) : null,
      cost: completeData.cost ? parseFloat(completeData.cost) : null,
      type: completeData.type,
      notes: completeData.notes,
      source: 'ai_recommendation',
    });
    setShowComplete(false);
    setExpanded(false);
  }

  function handleDismiss(reason) {
    dismissMaintenanceItem(vehicleId, item.service, reason);
    setDismissed(true);
    setShowIgnore(false);
    onToast?.(`"${item.service}" dismissed`);
  }

  if (dismissed) return null;

  return (
    <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          width: '100%',
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          textAlign: 'left',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontWeight: 600, fontSize: '0.9375rem' }}>{item.service}</span>
            <span className={`badge badge-${item.urgency}`}>{item.urgency}</span>
          </div>
        </div>
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--text-muted)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            transition: 'transform 0.2s',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0)',
            flexShrink: 0,
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {expanded && (
        <div
          style={{
            padding: '0 20px 16px',
            borderTop: '1px solid var(--border)',
            paddingTop: 16,
            animation: 'fadeIn 0.2s ease',
          }}
        >
          <p
            style={{
              fontSize: '0.8125rem',
              color: 'var(--text-secondary)',
              marginBottom: 16,
              lineHeight: 1.5,
            }}
          >
            {item.description}
          </p>

          {/* Cost comparison — DEALER / INDEPENDENT / DIY */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <CostPill label="Dealer" cost={item.costDealer} />
            <CostPill label="Independent" cost={item.costShop} />
            <CostPill label="DIY" cost={item.costDIY} />
          </div>

          {/* Difficulty */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <span
              style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                color: 'var(--text-muted)',
                textTransform: 'uppercase',
              }}
            >
              Difficulty:
            </span>
            <DifficultyDots level={item.difficulty} userLevel={skillLevel} />
            <span style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
              {item.difficulty}
            </span>
          </div>

          {/* Action buttons — Show Me the Steps (primary) + Mark Complete + Ignore */}
          {!showComplete && !showIgnore && (() => {
            const savedGuide = getSavedGuide(vehicleId, item.service);
            const hasSavedGuide = !!(savedGuide && savedGuide.guide);
            const savedProgress = hasSavedGuide ? (savedGuide.completedSteps || []) : [];
            const savedTotal = hasSavedGuide ? (savedGuide.guide.steps?.length || 0) : 0;
            const progressPct = savedTotal > 0 ? Math.round((savedProgress.length / savedTotal) * 100) : 0;

            return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {/* Show Me the Steps / Continue Guide — primary CTA */}
              <button
                onClick={() => onShowDIYGuide?.(item)}
                style={{
                  width: '100%',
                  padding: '11px 16px',
                  borderRadius: 'var(--radius-sm)',
                  border: 'none',
                  background: 'var(--accent)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  transition: 'all 0.15s ease',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {/* Progress bar background for saved guides */}
                {hasSavedGuide && progressPct > 0 && (
                  <div style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: `${progressPct}%`,
                    background: 'rgba(255,255,255,0.15)',
                    transition: 'width 0.3s',
                  }} />
                )}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'relative', zIndex: 1 }}>
                  {hasSavedGuide ? (
                    <>
                      <polyline points="9 18 15 12 9 6" />
                    </>
                  ) : (
                    <>
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="16" y1="13" x2="8" y2="13" />
                      <line x1="16" y1="17" x2="8" y2="17" />
                    </>
                  )}
                </svg>
                <span style={{ position: 'relative', zIndex: 1 }}>
                  {hasSavedGuide
                    ? `Continue Guide${progressPct > 0 ? ` (${progressPct}%)` : ''}`
                    : 'Show Me the Steps'}
                </span>
              </button>
              {/* Mark Complete + Ignore row — gated by canLog */}
              {canLog !== false && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    onClick={() => setShowComplete(true)}
                    style={{
                      flex: 1,
                      padding: '10px 16px',
                      borderRadius: 'var(--radius-sm)',
                      border: '1.5px solid var(--success, #22c55e)',
                      background: 'transparent',
                      color: 'var(--success, #22c55e)',
                      fontWeight: 600,
                      fontSize: '0.8125rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Mark Complete
                  </button>
                  <button
                    onClick={() => setShowIgnore(true)}
                    style={{
                      padding: '10px 16px',
                      borderRadius: 'var(--radius-sm)',
                      border: '1.5px solid var(--border)',
                      background: 'transparent',
                      color: 'var(--text-muted)',
                      fontWeight: 600,
                      fontSize: '0.8125rem',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 6,
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                    Ignore
                  </button>
                </div>
              )}
            </div>
            );
          })()}

          {/* Ignore reasons */}
          {showIgnore && (
            <div
              style={{
                background: 'var(--bg-primary)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                padding: 16,
              }}
            >
              <div style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: 10, color: 'var(--text-primary)' }}>
                Why are you ignoring this?
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {DISMISS_REASONS.map((reason) => (
                  <button
                    key={reason}
                    onClick={() => handleDismiss(reason)}
                    style={{
                      padding: '10px 14px',
                      fontSize: '0.8125rem',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-elevated)',
                      color: 'var(--text-secondary)',
                      cursor: 'pointer',
                      textAlign: 'left',
                      transition: 'all 0.15s',
                    }}
                  >
                    {reason}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setShowIgnore(false)}
                style={{
                  marginTop: 8,
                  padding: '8px 12px',
                  fontSize: '0.8125rem',
                  borderRadius: 'var(--radius-sm)',
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  width: '100%',
                }}
              >
                Cancel
              </button>
            </div>
          )}

          {/* Mark Complete form */}
          {showComplete && (
            <form
              onSubmit={handleSubmitComplete}
              style={{
                background: 'var(--bg-primary)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                padding: 16,
              }}
            >
              <div
                style={{
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  marginBottom: 12,
                  color: 'var(--text-primary)',
                }}
              >
                Log this service
              </div>

              <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={miniLabelStyle}>Date</label>
                  <input
                    className="form-input"
                    type="date"
                    value={completeData.date}
                    onChange={(e) =>
                      setCompleteData({ ...completeData, date: e.target.value })
                    }
                    style={{ ...miniInputStyle, colorScheme: 'dark' }}
                    required
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={miniLabelStyle}>Mileage</label>
                  <input
                    className="form-input"
                    type="text"
                    inputMode="numeric"
                    placeholder="e.g. 25000"
                    value={completeData.mileage}
                    onChange={(e) =>
                      setCompleteData({ ...completeData, mileage: e.target.value })
                    }
                    style={miniInputStyle}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                <div style={{ flex: 1 }}>
                  <label style={miniLabelStyle}>Cost ($)</label>
                  <input
                    className="form-input"
                    type="text"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={completeData.cost}
                    onChange={(e) =>
                      setCompleteData({ ...completeData, cost: e.target.value })
                    }
                    style={miniInputStyle}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={miniLabelStyle}>Done by</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {SERVICE_TYPES.map((t) => (
                      <button
                        key={t}
                        type="button"
                        onClick={() =>
                          setCompleteData({ ...completeData, type: t })
                        }
                        style={{
                          flex: 1,
                          padding: '7px 4px',
                          fontSize: '0.6875rem',
                          fontWeight: 600,
                          borderRadius: 'var(--radius-sm)',
                          border:
                            completeData.type === t
                              ? '1.5px solid var(--accent)'
                              : '1.5px solid var(--border)',
                          background:
                            completeData.type === t
                              ? 'var(--accent-subtle)'
                              : 'var(--bg-elevated)',
                          color:
                            completeData.type === t
                              ? 'var(--accent)'
                              : 'var(--text-secondary)',
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                        }}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={miniLabelStyle}>Notes (optional)</label>
                <input
                  className="form-input"
                  type="text"
                  placeholder="e.g. Used OEM parts, 2yr warranty"
                  value={completeData.notes}
                  onChange={(e) =>
                    setCompleteData({ ...completeData, notes: e.target.value })
                  }
                  style={miniInputStyle}
                />
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => setShowComplete(false)}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    fontSize: '0.8125rem',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border)',
                    background: 'transparent',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    borderRadius: 'var(--radius-sm)',
                    border: 'none',
                    background: 'var(--success, #22c55e)',
                    color: 'white',
                    cursor: 'pointer',
                  }}
                >
                  Save to Log
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}

const miniLabelStyle = {
  display: 'block',
  fontSize: '0.6875rem',
  fontWeight: 600,
  color: 'var(--text-muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  marginBottom: 4,
};

const miniInputStyle = {
  fontSize: '0.8125rem',
  padding: '8px 10px',
};

function CostPill({ label, cost }) {
  return (
    <div
      style={{
        flex: 1,
        textAlign: 'center',
        padding: '10px 8px',
        borderRadius: 'var(--radius-sm)',
        background: 'var(--bg-primary)',
        border: '1px solid var(--border)',
      }}
    >
      <div
        style={{
          fontSize: '0.6875rem',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: '0.875rem',
          fontWeight: 600,
          color: 'var(--text-primary)',
        }}
      >
        {cost}
      </div>
    </div>
  );
}

const LEVELS = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];

function DifficultyDots({ level }) {
  const idx = LEVELS.indexOf(level);
  return (
    <div style={{ display: 'flex', gap: 3 }}>
      {LEVELS.map((_, i) => (
        <div
          key={i}
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            background: i <= idx ? 'var(--accent)' : 'var(--bg-elevated)',
          }}
        />
      ))}
    </div>
  );
}
